import React, { FunctionComponent, SyntheticEvent, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { Container, Grid, Header } from 'semantic-ui-react';
import { useMutation } from '@apollo/react-hooks';
import withStyles from 'react-jss';
import classnames from 'classnames';
import dateFnsFormat from 'date-fns/format';
import { format } from 'date-fns';
import { pathOr } from 'ramda';

import { ClassProp } from 'helpers/types';
import FormDateInput from 'components/FormComponents/FormDateInput';
import FormInput from 'components/FormComponents/FormInput';
import FormTimeInput from 'components/FormComponents/FormTimeInput';
import {
  handleError,
  handleSuccess,
} from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/MerchantOffer.helpers';
import { DATE_FORMAT, buildTimeStamp, cstToUTC } from 'helpers/date';
import {
  OfferSummary,
  Segment,
} from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/MerchantOffer.types';

import { GET_OFFER } from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/scenes/PulseOffer/PulseOffer.queries';

import styles from './TerminateSegment.styles';
import TERMINATE_SEGMENT from './TerminateSegment.mutations';

type Styles = ClassProp<ReturnType<typeof styles>>;

type Props = {
  classes: Styles;
  segment: Segment;
  offerUuid: string;
  cancelUpdate: () => void;
};

type TerminateSegmentResponse = {
  offer: OfferSummary;
};

type TerminateSegmentFormData = {
  currentOfferSegmentUuid: string;
  endDate: Date;
  endTime: string;
  syncOfferEndDate: boolean;
};

const defaultSuccessMessage = [
  {
    displayMessage: 'Success!',
    message: 'Segment termination scheduled successfully.',
  },
];

const TerminateSegment: FunctionComponent<Props> = ({
  segment,
  offerUuid,
  cancelUpdate,
  classes,
}) => {
  const { uuid } = segment;
  const defaultEndDate = new Date();
  const methods = useForm<TerminateSegmentFormData>({
    mode: 'onBlur',
    defaultValues: {
      endDate: defaultEndDate,
      endTime: dateFnsFormat(defaultEndDate, 'HH:mm'),
    },
  });

  const { register, handleSubmit } = methods;

  useEffect(() => {
    register(
      { name: 'endDate' },
      {
        required: 'End Date is required',
      },
    );
    register({ name: 'endTime' }, { required: 'End Time is required' });
  }, [register]);

  const handleTerminateSegmentResponse = (
    response: TerminateSegmentResponse,
  ) => {
    const success = pathOr(false, ['terminateSegment', 'offerUuid'], response);

    if (success) {
      handleSuccess(defaultSuccessMessage);
      cancelUpdate();
    }
  };

  const [runMutation, { loading: isSubmitting }] = useMutation(
    TERMINATE_SEGMENT,
    {
      onCompleted: handleTerminateSegmentResponse,
      onError: handleError,
      refetchQueries: [
        {
          query: GET_OFFER,
          variables: {
            params: {
              offerUuid,
              hydrateFinancialTerms: true,
            },
          },
        },
      ],
    },
  );

  const handleClick = (_: SyntheticEvent<HTMLElement, Event>) => cancelUpdate();

  const onSubmit = ({
    currentOfferSegmentUuid,
    endDate,
    endTime,
    syncOfferEndDate,
  }: TerminateSegmentFormData) => {
    const endTimeStamp = buildTimeStamp(format(endDate, DATE_FORMAT), endTime);

    const terminateInput = {
      currentOfferSegmentUuid,
      syncOfferEndDate,
      dryRun: false,
      endDate: cstToUTC(endTimeStamp),
    };

    runMutation({
      variables: {
        params: { ...terminateInput },
      },
    });

    return;
  };

  return (
    <Container className={classes.container}>
      <Header as="h2">Terminate Segment</Header>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <input
            type="hidden"
            value={uuid}
            name="currentOfferSegmentUuid"
            ref={register}
          />
          <Grid>
            <Grid.Row>
              <Grid.Column width={4}>
                <FormDateInput
                  name="endDate"
                  label="End Date"
                  placeholder={defaultEndDate}
                />
              </Grid.Column>
              <Grid.Column width={4}>
                <FormTimeInput
                  name="endTime"
                  label="End Time (CST)"
                  placeholder={defaultEndDate}
                />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column width={8}>
                <FormInput
                  name="syncOfferEndDate"
                  type="checkbox"
                  placeholder="Sync Offer End Date?"
                  label="Sync Offer End Date?"
                  register={register}
                />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row className={classes.formRow}>
              <button
                className={classnames(
                  classes.button,
                  classes.buttonSecondary,
                  classes.mediumBtn,
                )}
                onClick={handleClick}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className={classnames(classes.button, classes.mediumBtn)}
              >
                {isSubmitting ? '...Submitting' : 'Save'}
              </button>
            </Grid.Row>
          </Grid>
        </form>
      </FormProvider>
    </Container>
  );
};

export default withStyles(styles)(TerminateSegment);
